import { AgGridSelectedRowsContext } from '@cfra-nextgen-frontend/shared/src/components/AgGrid/AgGridSelectedRowsContext/AgGridSelectedRowsContext';
import { ColumnDef } from '@cfra-nextgen-frontend/shared/src/components/AgGrid/types';
import { getSnapshot, subscribe } from '@cfra-nextgen-frontend/shared/src/hooks/windowDimensions';
import { ValueTypes, formatValue } from '@cfra-nextgen-frontend/shared/src/utils/valuesFormatter';
import {
    AllCommunityModule,
    BodyScrollEvent,
    ColDef,
    Column,
    ColumnResizedEvent,
    ColumnState,
    CsvExportParams,
    DefaultMenuItem,
    GetMainMenuItems,
    GridApi,
    IServerSideDatasource,
    ModelUpdatedEvent,
    ModuleRegistry,
    RowClassRules,
    RowHeightParams,
    SortChangedEvent,
    ValueFormatterParams,
    provideGlobalGridOptions,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import {
    ColumnMenuModule,
    ContextMenuModule,
    ServerSideRowModelApiModule,
    ServerSideRowModelModule,
} from 'ag-grid-enterprise';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { debounce } from 'lodash';
import {
    forwardRef,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import {
    AgGridEachTypeExtension,
    AgGridPreferencesActions,
    GeneralPreferencesConfiguration,
    PreferenceType,
} from '../../types/userPreferences';
import { isTouchDevice } from '../../utils/touchScreen';
import { UserPreferences, UserPreferencesRef } from '../UserPreferences/UserPreferences';
import {
    saveUserColumnsOrder,
    saveUserColumnsVisibility,
    saveUserColumnsWidth,
    saveUserSortModel,
    UserPreferencesSavingModes,
} from '../UserPreferences/utils/agGrid';
import { AgGridDragScroll, AgGridDragScrollRef } from './AgGridDragScroll';
import { AgGridHorizontalScrollArrows, AgGridHorizontalScrollArrowsRef } from './AgGridHorizontalScrollArrows';
import './scss/AgGrid.scss';
import './scss/AgMenu.scss';
import './scss/AgPanel.scss';
import './scss/IconClasses.scss';
import './scss/Paddings.scss';
import './scss/UnsortIconStyles.scss';
import { agGridGetRenderedRowsCount, disableColumnFlexOnResize, getClassNameValue, getDefaultColDef } from './utils';

// Register all community features
ModuleRegistry.registerModules([
    AllCommunityModule,
    ColumnMenuModule,
    ServerSideRowModelModule,
    ContextMenuModule,
    ServerSideRowModelApiModule,
]);

// Mark all grids as using legacy themes
provideGlobalGridOptions({ theme: 'legacy' });

export function getAgGridFormatter(formattingType: ValueTypes) {
    return function (params: ValueFormatterParams) {
        return String(
            formatValue({
                value: params.value,
                formattingType: formattingType,
            }),
        );
    };
}

export type ColumnAndGridApi = { api: GridApi };

function getColumnAndGridApiObject(eventObject?: ColumnAndGridApi, refObject?: ColumnAndGridApi) {
    if (eventObject?.api) {
        return eventObject;
    }

    return refObject;
}

export type AgGridProps = {
    columnDefs: Array<ColumnDef>;
    rowsData?: Array<any>;
    getResizableMinWidthForColumn: (key: string) => number;
    updateCustomColumnsWidths?: (agGridObject: ColumnAndGridApi) => void;
    maxNumberOfRowsToDisplay?: number;
    maxGridContainerHeightPercentage?: number; // the value in percentage from window height [1;100], needed for cases when grid container is centered, for example if grid is placed inside modal window
    useSSRMode?: boolean;
    SSRDataSource?: IServerSideDatasource;
    SSRrowsToFetch?: number;
    rowMultiSelectWithClick?: boolean;
    rowSelection?: 'singleRow' | 'multiRow';
    getRowID?: (params: any) => string;
    suppressRowClickSelection?: boolean;
    useAutoHeight?: boolean;
    gridTheme?: string | Array<string>;
    defaultRowHeight?: number;
    headerHeight?: number;
    defaultMaxWidth?: number | null;
    unlimitedCalculatedHeight?: boolean;
    rowsGap?: number;
    lastRowAdditionalWidth?: number; // needed to not cut off shadow for last row;
    horizontalScrollbarAreaHeight?: number;
    customFlexibleColumns?: Array<string>;
    getRowHeight?: (params: RowHeightParams) => number | undefined | null;
    enableAutoSizeAllColumns?: boolean;
    onFirstDataRenderedRef?: React.MutableRefObject<() => void>;
    onModelUpdatedRef?: React.MutableRefObject<(event: ModelUpdatedEvent) => void>;
    onSortChangedRef?: React.MutableRefObject<(event: SortChangedEvent) => void>;
    fullHeightGrid?: boolean;
    embedFullWidthRows?: boolean;
    defaultCsvExportParams?: CsvExportParams;
    rowClassRules?: RowClassRules<any>;
    showSideHorizontalScrollIndicators?: boolean;
    useDragScroll?: boolean;
    useBrowserVerticalScroll?: boolean;
    onColumnMovedGetter?: (gridContainerRef: React.RefObject<HTMLDivElement>) => AgGridReactProps['onColumnMoved'];
    onColumnVisibleGetter?: (gridContainerRef: React.RefObject<HTMLDivElement>) => AgGridReactProps['onColumnVisible'];
    autoSizePadding?: number;
    tooltipShowDelay?: number;
    selectionCheckboxWidth?: number;
    checkColumnDefsEqual?: (newColumnDefs?: Array<ColDef>, currentColumnDefs?: Array<ColDef>) => boolean;
    applySortState?: boolean;
    autosizeColumnsConfig?: {
        skipHeader?: boolean;
        skipHasPinnedColumnsCheck?: boolean;
    };
    rowBuffer?: number;
    suppressHeaderMenuButton?: boolean;
    suppressHeaderFilterButton?: boolean;
    suppressHeaderContextMenu?: boolean;
    resetColumnsCallbackRef?: React.MutableRefObject<() => void>;
    preferencesConfiguration?: GeneralPreferencesConfiguration;
    enableSavingUserSortModel?: boolean;
    enableSavingUserColumnsOrder?: boolean;
    enableSavingUserColumnsVisibility?: boolean;
    enableSavingUserColumnsWidths?: boolean;
    sortOnReset?: {
        colId: string;
        sort: 'asc' | 'desc' | null;
    };
    setUserPreferencesOnReset?: ReturnType<typeof useUserPreferences>['setUserPreferences'];
    useColumnWidthsFromColumnDefs?: boolean;
    useDisableColumnFlexOnResize?: boolean;
    userPreferencesSavingMode?: UserPreferencesSavingModes;
    debouncedAutoSizeAllColumnsDelay?: number;
    onGridReady?: (gridRef: RefObject<AgGridReact<any>> | RefObject<AgGridExtendedRef>) => void;
    tooltipInteraction?: boolean;
};

export const defaultMinWidth = 120;
export const defaultAgGridCardRowHeight = 46;
const unlimitedResizeColumnWidth = 9999;

export type AgGridExtendedRef =
    | ({
          updateGridTopPosition?: () => void;
          updateGridHeight?: () => void;
      } & AgGridReact)
    | null;

export const AgGrid = forwardRef<AgGridExtendedRef | null, AgGridProps>((props, ref) => {
    const onModelUpdatedRefTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { onRowSelected, setGridRef } = useContext(AgGridSelectedRowsContext);
    const { height } = useSyncExternalStore(subscribe, getSnapshot);
    const [gridTopPosition, setGridTopPosition] = useState<number>();
    const gridRef = useRef<AgGridReact>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const setDragListenersRef = useRef<AgGridDragScrollRef>(null);
    const horizontalScrollArrowsRef = useRef<AgGridHorizontalScrollArrowsRef>(null);
    const [gridApi, setGridApi] = useState<GridApi<any>>();
    const [gridHeight, setGridHeight] = useState<number>(0);

    // just in case to avoid memory leaks
    useEffect(() => {
        return () => {
            setGridApi(undefined);
        };
    }, []);

    const updateGridTopPosition = useCallback(() => {
        const gridContainerRefCurrent = gridContainerRef.current;
        if (gridContainerRefCurrent) {
            const { top } = gridContainerRefCurrent.getBoundingClientRect();
            setGridTopPosition(top);
        }
    }, [gridContainerRef]);

    useEffect(() => {
        updateGridTopPosition();
    }, [updateGridTopPosition]);

    const {
        rowsData = [],
        getResizableMinWidthForColumn,
        updateCustomColumnsWidths,
        maxNumberOfRowsToDisplay = 20,
        maxGridContainerHeightPercentage,
        useSSRMode = false,
        useAutoHeight = false,
        gridTheme,
        defaultRowHeight = defaultAgGridCardRowHeight,
        headerHeight,
        defaultMaxWidth = 350,
        unlimitedCalculatedHeight,
        rowsGap,
        lastRowAdditionalWidth,
        horizontalScrollbarAreaHeight = 18,
        customFlexibleColumns,
        getRowHeight,
        enableAutoSizeAllColumns = true,
        onFirstDataRenderedRef,
        onModelUpdatedRef,
        onSortChangedRef,
        fullHeightGrid,
        embedFullWidthRows,
        defaultCsvExportParams,
        showSideHorizontalScrollIndicators,
        useDragScroll,
        useBrowserVerticalScroll,
        onColumnMovedGetter,
        onColumnVisibleGetter,
        autoSizePadding,
        tooltipShowDelay = 0,
        selectionCheckboxWidth = 20,
        checkColumnDefsEqual,
        applySortState = true,
        autosizeColumnsConfig,
        rowBuffer,
        suppressHeaderMenuButton,
        suppressHeaderFilterButton,
        suppressHeaderContextMenu,
        resetColumnsCallbackRef,
        preferencesConfiguration,
        enableSavingUserSortModel,
        enableSavingUserColumnsOrder,
        enableSavingUserColumnsVisibility,
        enableSavingUserColumnsWidths,
        sortOnReset,
        setUserPreferencesOnReset,
        useColumnWidthsFromColumnDefs: _useColumnWidthsFromColumnDefs,
        useDisableColumnFlexOnResize,
        userPreferencesSavingMode,
        debouncedAutoSizeAllColumnsDelay,
        tooltipInteraction = false,
    } = props;

    const useColumnWidthsFromColumnDefs = useMemo(() => {
        if (!_useColumnWidthsFromColumnDefs) {
            return false;
        }

        if (!preferencesConfiguration) {
            return false;
        }

        return props.columnDefs.some((columnDef) => columnDef.width !== undefined);
    }, [_useColumnWidthsFromColumnDefs, preferencesConfiguration, props.columnDefs]);

    const enableAutoSizeAllColumnsRefMutable = useRef<boolean>(true);

    const calculateHeight = useCallback(() => {
        const windowHeight = height || 0;

        let rowsToDisplay: number = 0;

        if (useSSRMode) {
            rowsToDisplay = props.SSRrowsToFetch || 0;
        } else {
            rowsToDisplay = fullHeightGrid && gridApi ? gridApi?.getDisplayedRowCount?.() : rowsData.length;
        }

        const numberOfRowsToShow = rowsToDisplay > maxNumberOfRowsToDisplay ? maxNumberOfRowsToDisplay : rowsToDisplay; // show vertical scroll if more than maxNumberOfRowsToDisplay rows

        const tableBodyHeight =
            horizontalScrollbarAreaHeight +
            numberOfRowsToShow * defaultRowHeight +
            (rowsGap || 0) * (numberOfRowsToShow - 1) +
            (lastRowAdditionalWidth || 0);

        const staticGridHeight = headerHeight ? tableBodyHeight + headerHeight : tableBodyHeight + defaultRowHeight;

        if (unlimitedCalculatedHeight) {
            return staticGridHeight;
        }

        const _gridTopPosition = gridTopPosition || 0;

        if (maxGridContainerHeightPercentage) {
            const maxGridContainerHeight = (windowHeight * maxGridContainerHeightPercentage) / 100;
            const gridContainerSpaceAboveTheGrid = _gridTopPosition - (windowHeight - maxGridContainerHeight) / 2;
            const maxGridHeight = maxGridContainerHeight - gridContainerSpaceAboveTheGrid;

            return staticGridHeight < maxGridHeight ? staticGridHeight : maxGridHeight;
        }

        return staticGridHeight < windowHeight - _gridTopPosition ? staticGridHeight : windowHeight - _gridTopPosition;
    }, [
        height,
        maxNumberOfRowsToDisplay,
        rowsData,
        useSSRMode,
        props.SSRrowsToFetch,
        defaultRowHeight,
        headerHeight,
        rowsGap,
        lastRowAdditionalWidth,
        horizontalScrollbarAreaHeight,
        gridTopPosition,
        maxGridContainerHeightPercentage,
        unlimitedCalculatedHeight,
        gridApi,
        fullHeightGrid,
    ]);

    const updateGridHeight = useCallback(() => {
        const calculatedHeight = calculateHeight();
        const minGridHeight =
            5 * defaultRowHeight + // 4 rows + header
            horizontalScrollbarAreaHeight;
        const gridHeight = calculatedHeight < minGridHeight ? minGridHeight : calculatedHeight;

        setGridHeight(gridHeight);
    }, [calculateHeight, defaultRowHeight, horizontalScrollbarAreaHeight]);

    useEffect(() => {
        updateGridHeight();
    }, [updateGridHeight]);

    useImperativeHandle(ref, () => {
        if (!gridRef.current) {
            return {} as NonNullable<AgGridExtendedRef>;
        }

        return {
            ...gridRef.current,
            updateGridTopPosition,
        } as NonNullable<AgGridExtendedRef>;
    });

    const sortState = useMemo(
        () =>
            props.columnDefs
                .filter((colDef) => colDef.field && colDef.sort) // get only the columns that have sort enabled
                .map((colDef) => ({ colId: colDef.field!, sort: colDef.sort! })), // add sort to column state;
        [props.columnDefs],
    );

    const columnDefsRef = useRef<AgGridProps['columnDefs']>(props.columnDefs);

    const columnDefs = useMemo(
        () =>
            props.columnDefs?.map((columnDefs) => ({
                minWidth: columnDefs.checkboxSelection
                    ? selectionCheckboxWidth
                    : getResizableMinWidthForColumn(String(columnDefs.headerName)),
                ...columnDefs,
                ...(applySortState ? { sort: null } : {}), // remove sort from columnDefs, to avoid grid sort issues, it will be applied separately
                // if preset column width are used, the autosize columns is disabled, and so we need to set maxWidth to enable column resizing
                // and need to set flex to false to apply the width from columnDefs
                ...(useColumnWidthsFromColumnDefs ? { maxWidth: unlimitedResizeColumnWidth, flex: undefined } : {}),
            })),
        [
            getResizableMinWidthForColumn,
            props.columnDefs,
            selectionCheckboxWidth,
            applySortState,
            useColumnWidthsFromColumnDefs,
        ],
    );

    useEffect(() => {
        // Check if gridRef is available and the grid API is ready
        if (!gridRef.current || !gridRef.current.api) {
            return;
        }

        // Retrieve current column definitions
        const currentColumnDefs = gridRef.current.api.getColumnDefs();

        // Do nothing the column definitions are already set and the columns set is the same
        if (columnDefs.length === 0 || checkColumnDefsEqual?.(columnDefs, currentColumnDefs)) {
            return;
        }

        gridRef.current.api.setGridOption('columnDefs', columnDefs);
    }, [columnDefs, gridRef.current, checkColumnDefsEqual]); // keep gridRef.current in dependencies to avoid empty grid on navigate to page

    useEffect(() => {
        if (!gridRef.current || !applySortState) return;
        gridRef.current?.api?.applyColumnState({ state: sortState });
    }, [sortState, gridRef.current, applySortState]); // keep gridRef.current in dependencies to avoid empty grid on navigate to page

    const firstRenderColumnDefs = useMemo(() => columnDefs, []); // keep here no dependencies, the firstRenderColumnDefs should update only first time

    const className = useMemo(() => getClassNameValue(gridTheme), [gridTheme]);

    // DefaultColDef sets props common to all Columns
    const defaultColDef = useMemo((): ColDef => {
        return {
            ...getDefaultColDef({ className, defaultMaxWidth }),
            ...(suppressHeaderMenuButton !== undefined ? { suppressHeaderMenuButton } : {}),
            ...(suppressHeaderFilterButton !== undefined ? { suppressHeaderFilterButton } : {}),
            ...(suppressHeaderContextMenu !== undefined ? { suppressHeaderContextMenu } : {}),
        };
    }, [defaultMaxWidth, className, suppressHeaderMenuButton, suppressHeaderFilterButton, suppressHeaderContextMenu]);

    const setMaxResizeWidthForAllColumnsTo = useCallback((maxWidth: number, agGridObject?: ColumnAndGridApi) => {
        const agGrid = agGridObject || gridRef.current;

        const columnDefsLocal: Array<ColDef> = agGrid?.api.getColumnDefs() as Array<ColDef>;
        if (!columnDefsLocal) {
            return;
        }
        agGrid?.api.setGridOption(
            'columnDefs',
            columnDefsLocal.map((columnDef) => ({ ...columnDef, maxWidth: maxWidth })),
        );
    }, []);

    const setMinResizeWidthForAllColumns = useCallback(
        (agGridObject?: ColumnAndGridApi) => {
            const agGrid = agGridObject || gridRef.current;
            const columnDefsLocal = agGrid?.api.getColumnDefs();

            if (!columnDefsLocal) {
                return;
            }

            agGrid?.api.setGridOption(
                'columnDefs',
                columnDefsLocal.map((columnDef) => ({
                    ...columnDef,
                    minWidth: (columnDef as ColDef).checkboxSelection
                        ? selectionCheckboxWidth
                        : getResizableMinWidthForColumn(String(columnDef.headerName)),
                })),
            );
        },
        [getResizableMinWidthForColumn, selectionCheckboxWidth],
    );

    const setInitialColumnDefsForColumns = useCallback(
        ({
            keys,
            agGridObject,
            columnField,
        }: {
            keys: Array<string>;
            agGridObject?: ColumnAndGridApi;
            columnField?: string;
        }) => {
            const agGrid = agGridObject || gridRef.current;

            const localColumnDefs: Array<ColumnDef> = agGrid?.api.getColumnDefs() as Array<ColumnDef>;

            if (!localColumnDefs) {
                return;
            }

            // if we use preset columns width, we need props.columnDefs to extract original maxWidth for the columns
            if (!columnDefsRef.current && useColumnWidthsFromColumnDefs) {
                return;
            }

            agGrid?.api.setGridOption(
                'columnDefs',
                localColumnDefs.map((localColumnDef) => {
                    keys.forEach((key) => {
                        const colDefProp = columnDefs.filter((item) => item.field === localColumnDef.field)[0];

                        if (columnField && colDefProp.field !== columnField) {
                            return;
                        }

                        localColumnDef = {
                            ...localColumnDef,
                            [key]:
                                colDefProp && colDefProp.hasOwnProperty(key)
                                    ? colDefProp[key as keyof typeof colDefProp]
                                    : defaultColDef[key as keyof typeof defaultColDef],
                        };
                    });

                    if (keys.includes('maxWidth') && useColumnWidthsFromColumnDefs) {
                        const colDefProp = columnDefsRef.current.find(
                            (columnDef) => columnDef.field === localColumnDef.field,
                        );
                        if (colDefProp) {
                            localColumnDef.maxWidth = colDefProp.maxWidth;
                            localColumnDef.flex = colDefProp.flex;
                        }
                    }

                    return localColumnDef;
                }),
            );
        },
        [columnDefs, defaultColDef, useColumnWidthsFromColumnDefs],
    );

    function calculateAllColumnsWidths() {
        return (
            gridRef.current?.api
                .getColumns()
                ?.reduce((prevResult, current) => (prevResult += current.getActualWidth()), 0) || 0
        );
    }

    const autoSizeAllColumns = useCallback(
        ({
            params,
            columnField,
            forceEnableAutoSizeAllColumns,
        }: {
            params: ColumnAndGridApi;
            columnField?: string;
            forceEnableAutoSizeAllColumns?: boolean;
        }) => {
            const agGridObject = params || (gridRef.current as ColumnAndGridApi);

            if (
                !agGridObject ||
                !columnDefs ||
                columnDefs.length === 0 ||
                (forceEnableAutoSizeAllColumns === undefined && !enableAutoSizeAllColumns) ||
                (forceEnableAutoSizeAllColumns === undefined && !enableAutoSizeAllColumnsRefMutable.current) ||
                (forceEnableAutoSizeAllColumns !== undefined && !forceEnableAutoSizeAllColumns) ||
                (forceEnableAutoSizeAllColumns === undefined && useColumnWidthsFromColumnDefs)
            ) {
                return;
            }

            const keys = ['maxWidth', 'minWidth'];

            setInitialColumnDefsForColumns({ keys, agGridObject, columnField });

            const columnIds: Array<Column> = [];
            agGridObject?.api?.getColumns()?.forEach((column) => {
                const colDef = column.getColDef();

                if (columnField && colDef?.field !== columnField) {
                    return;
                }

                if (
                    !customFlexibleColumns ||
                    !(colDef?.headerName && customFlexibleColumns.includes(colDef?.headerName))
                ) {
                    columnIds.push(column);
                    return;
                }
            });

            agGridObject?.api?.autoSizeColumns(columnIds, autosizeColumnsConfig?.skipHeader || true);

            if (!autosizeColumnsConfig?.skipHasPinnedColumnsCheck) {
                const hasPinnedColumns = agGridObject.api
                    .getColumnDefs()
                    ?.map((colDef) => (colDef as ColumnDef).pinned)
                    .some((x: any) => Boolean(x));

                if (!hasPinnedColumns) {
                    const right = agGridObject?.api?.getHorizontalPixelRange()?.right;
                    const actualWidthExceededViewableWidth = calculateAllColumnsWidths() > right;
                    if (actualWidthExceededViewableWidth) {
                        agGridObject?.api.autoSizeColumns(columnIds, false);
                    }
                }
            }

            if (updateCustomColumnsWidths) {
                updateCustomColumnsWidths(agGridObject);
            }

            setMaxResizeWidthForAllColumnsTo(unlimitedResizeColumnWidth, agGridObject);
            setMinResizeWidthForAllColumns(agGridObject);
        },
        [
            setInitialColumnDefsForColumns,
            setMinResizeWidthForAllColumns,
            updateCustomColumnsWidths,
            customFlexibleColumns,
            columnDefs,
            enableAutoSizeAllColumns,
            autosizeColumnsConfig?.skipHeader,
            autosizeColumnsConfig?.skipHasPinnedColumnsCheck,
            useColumnWidthsFromColumnDefs,
            setMaxResizeWidthForAllColumnsTo,
        ],
    );

    const debouncedAutoSizeAllColumns = useMemo(() => {
        // if we use SSR mode, we need to wait until the first portion of data is loaded
        // otherwise, autosize columns will works as there would be no data in the grid
        const _debouncedAutoSizeAllColumns = debounce(
            ({
                event,
                columnField,
                forceEnableAutoSizeAllColumns,
            }: {
                event?: ColumnAndGridApi;
                columnField?: string;
                forceEnableAutoSizeAllColumns?: boolean;
            }) => {
                const agGrid = getColumnAndGridApiObject(event, gridRef.current as ColumnAndGridApi);

                if (!agGrid) {
                    return;
                }

                const currentRowCount = agGridGetRenderedRowsCount(agGrid as AgGridReact); // Get the current number of displayed rows
                // don't autosize columns if no rows displayed
                if (
                    !forceEnableAutoSizeAllColumns &&
                    (currentRowCount === 1 || // should be useful for the client side rendering mode
                        // for the SSR mode, we need to wait for the first portion of data to be loaded
                        (currentRowCount === 1 && useSSRMode && props.SSRrowsToFetch && props.SSRrowsToFetch > 1))
                ) {
                    return;
                }

                autoSizeAllColumns({
                    params: agGrid,
                    columnField,
                    forceEnableAutoSizeAllColumns,
                });
            },
            debouncedAutoSizeAllColumnsDelay || 500,
        );
        return _debouncedAutoSizeAllColumns;
    }, [autoSizeAllColumns, props.SSRrowsToFetch, useSSRMode, debouncedAutoSizeAllColumnsDelay]);

    const onBodyScroll = useCallback(
        (event: BodyScrollEvent): void => {
            if (showSideHorizontalScrollIndicators && event.direction === 'horizontal') {
                horizontalScrollArrowsRef.current?.refreshHorizontalScrollIndicators();
                horizontalScrollArrowsRef.current?.updateArrowsVerticalPositions();
            }
        },
        [showSideHorizontalScrollIndicators],
    );

    const onColumnMoved = useMemo(() => {
        const _onColumnMoved = onColumnMovedGetter?.(gridContainerRef);
        return _onColumnMoved
            ? debounce((event) => {
                  if (
                      preferencesConfiguration &&
                      enableSavingUserColumnsOrder &&
                      ['uiColumnMoved', 'toolPanelUi'].includes(event.source)
                  ) {
                      saveUserColumnsOrder({
                          api: event.api,
                          setUserPreferences: userPreferencesRef.current?.setUserPreferences,
                          selector: preferencesConfiguration?.selectorConfiguration?.selector,
                          userPreferencesSavingMode,
                      });
                  }

                  _onColumnMoved?.(event);
              }, 100)
            : undefined;
    }, [
        onColumnMovedGetter,
        gridContainerRef,
        preferencesConfiguration,
        enableSavingUserColumnsOrder,
        userPreferencesSavingMode,
    ]);

    useEffect(() => {
        return () => {
            if (onColumnMoved) {
                onColumnMoved.cancel();
            }
        };
    }, [onColumnMoved]);

    const onSortChanged = useCallback(
        (event: SortChangedEvent) => {
            if (enableSavingUserSortModel && ['columnMenu', 'uiColumnSorted'].includes(event.source)) {
                saveUserSortModel({
                    api: event.api,
                    setUserPreferences: userPreferencesRef.current?.setUserPreferences,
                    selector: preferencesConfiguration?.selectorConfiguration?.selector,
                });
            }

            onSortChangedRef?.current(event);
        },
        [enableSavingUserSortModel, preferencesConfiguration?.selectorConfiguration?.selector, onSortChangedRef],
    );

    const getMainMenuItems: GetMainMenuItems = useCallback(
        (params) => {
            const sortEvent = {
                api: params.api,
                type: 'sortChanged' as const,
                source: 'columnMenu',
                context: undefined,
            };

            const columnState = params.api.getColumnState();

            const changeSortState = (sort?: 'asc' | 'desc', updatedColumnState?: Array<ColumnState>) => {
                const _updatedColumnState = columnState.map((columnStateItem) => {
                    if (columnStateItem.colId === params.column?.getColDef().colId) {
                        return { ...columnStateItem, sort };
                    }
                    return { ...columnStateItem, sort: null };
                });
                // this is necessary to have the sorting be applied in the grid state
                // make the behavior the same as when user clicked on the column header
                params.api.applyColumnState({ state: updatedColumnState || _updatedColumnState });

                // call everything else only after the sorting applied to the grid state
                onSortChanged(sortEvent);
            };

            const updatedItems: Array<DefaultMenuItem> = [
                ...(params.column?.getColDef().sortable === false
                    ? []
                    : ['sortAscending' as const, 'sortDescending' as const, 'separator' as const]),
                'autoSizeAll',
                'separator',
                'columnChooser',
                'resetColumns',
            ];

            return updatedItems.map((item) => {
                switch (item) {
                    case 'sortAscending':
                        if (params.column?.getColDef().sortable === false) {
                            return {
                                name: 'Sort Ascending',
                                disabled: true,
                            };
                        }
                        return {
                            name: 'Sort Ascending',
                            action: () => changeSortState('asc'),
                        };
                    case 'sortDescending':
                        return {
                            name: 'Sort Descending',
                            action: () => changeSortState('desc'),
                        };
                    case 'autoSizeAll':
                        return {
                            name: 'Autosize All Columns',
                            action: () =>
                                debouncedAutoSizeAllColumns({
                                    event: params,
                                    forceEnableAutoSizeAllColumns: true,
                                }),
                        };
                    case 'resetColumns':
                        return {
                            name: 'Reset Columns',
                            action: () => {
                                // for the cases using preferences to update column defs out of Ag Grid component during reset
                                if (preferencesConfiguration) {
                                    // it is necessary to use setUserPreferencesOnReset if re-render is required after click on reset
                                    (setUserPreferencesOnReset || userPreferencesRef.current?.setUserPreferences)?.<
                                        keyof typeof AgGridPreferencesActions,
                                        AgGridEachTypeExtension
                                    >(PreferenceType.AgGridPreferences, {
                                        ...preferencesConfiguration?.selectorConfiguration?.selector,
                                        action: AgGridPreferencesActions.ResetAllPreferences,
                                    });
                                }

                                // for the cases NOT using preferences to update column defs out of Ag Grid component during reset
                                // so columnDefs don't change outside of Ag Grid component during the reset
                                if (!preferencesConfiguration) {
                                    // show all hidden columns
                                    gridRef.current?.api.setGridOption('columnDefs', columnDefsRef.current);
                                    // reset column state
                                    gridRef.current?.api.resetColumnState();
                                    // apply sort state
                                    changeSortState(undefined, sortOnReset ? [sortOnReset] : sortState);
                                }

                                // run external callback (for ex. for the case where sorting is handled outside)
                                resetColumnsCallbackRef?.current?.();
                                // autosize all columns
                                setTimeout(
                                    () =>
                                        debouncedAutoSizeAllColumns({
                                            event: params,
                                            forceEnableAutoSizeAllColumns: true,
                                        }),
                                    300,
                                );

                                // enable autosize all columns after reset
                                enableAutoSizeAllColumnsRefMutable.current = true;

                                if (preferencesConfiguration) {
                                    // we also need reset state to apply changes
                                    gridRef.current?.api.resetColumnState();
                                }
                            },
                        };
                }
                return item;
            });
        },
        [
            debouncedAutoSizeAllColumns,
            gridRef,
            resetColumnsCallbackRef,
            sortState,
            onSortChanged,
            sortOnReset,
            setUserPreferencesOnReset,
            preferencesConfiguration,
        ],
    );

    const userPreferencesRef = useRef<UserPreferencesRef>(null);

    const debouncedOnColumnResized = useMemo(
        () =>
            debounce((event: ColumnResizedEvent) => {
                if (preferencesConfiguration && enableSavingUserColumnsWidths && event.source === 'uiColumnResized') {
                    saveUserColumnsWidth({
                        api: event.api,
                        setUserPreferences: userPreferencesRef.current?.setUserPreferences,
                        selector: preferencesConfiguration?.selectorConfiguration?.selector,
                        userPreferencesSavingMode,
                    });
                }

                if (useDisableColumnFlexOnResize && event.source === 'uiColumnResized') {
                    disableColumnFlexOnResize(event);
                    // disable columns autosize after manual resize
                    enableAutoSizeAllColumnsRefMutable.current = false;
                }
            }, 200),
        [
            preferencesConfiguration,
            enableSavingUserColumnsWidths,
            useDisableColumnFlexOnResize,
            enableAutoSizeAllColumnsRefMutable,
            userPreferencesSavingMode,
        ],
    );

    return (
        <div ref={gridContainerRef} style={{ width: '100%' }} className='cfra-ag-grid'>
            <div
                className={className}
                style={{
                    height: useAutoHeight ? undefined : `${gridHeight}px`,
                    width: '100%',
                }}>
                <AgGridReact
                    ref={gridRef}
                    {...(useSSRMode
                        ? {
                              rowModelType: 'serverSide',
                              serverSideDatasource: props.SSRDataSource,
                              cacheBlockSize: props.SSRrowsToFetch,
                              getRowId: props.getRowID,
                              rowMultiSelectWithClick: props.rowMultiSelectWithClick,
                          }
                        : { rowData: rowsData })}
                    columnDefs={applySortState ? firstRenderColumnDefs : columnDefs}
                    defaultColDef={defaultColDef} // Default Column Properties
                    animateRows={true}
                    rowSelection={
                        props.suppressRowClickSelection
                            ? undefined
                            : {
                                  mode: props.rowSelection || 'singleRow',
                                  enableClickSelection: false,
                                  checkboxes: false,
                                  hideDisabledCheckboxes: true,
                                  headerCheckbox: false,
                              }
                    }
                    rowClassRules={props.rowClassRules}
                    getRowHeight={
                        getRowHeight ||
                        function (params) {
                            if (params.node.rowIndex === rowsData.length - 1) {
                                return defaultRowHeight + (lastRowAdditionalWidth || 0);
                            }
                            return defaultRowHeight + (rowsGap || 0);
                        }
                    }
                    getRowStyle={(params) => {
                        return {
                            maxHeight: getRowHeight?.(params) || defaultRowHeight,
                        };
                    }}
                    headerHeight={headerHeight || defaultRowHeight}
                    onModelUpdated={(event) => {
                        if (fullHeightGrid) {
                            updateGridHeight();
                        }

                        if (useSSRMode) {
                            // Get the current number of displayed rows
                            const currentRowCount = agGridGetRenderedRowsCount(event as unknown as AgGridReact);
                            // trigger autosize only for the first portion of data
                            if (
                                props.SSRrowsToFetch &&
                                currentRowCount > 1 &&
                                currentRowCount - 1 < props.SSRrowsToFetch
                            ) {
                                debouncedAutoSizeAllColumns({ event });
                            }
                        }

                        setDragListenersRef.current?.refreshListeners();

                        if (showSideHorizontalScrollIndicators) {
                            horizontalScrollArrowsRef.current?.refreshHorizontalScrollIndicators();
                            horizontalScrollArrowsRef.current?.updateArrowsVerticalPositions();
                        }

                        if (!onModelUpdatedRef?.current) {
                            return;
                        }

                        if (onModelUpdatedRefTimeout.current) {
                            clearTimeout(onModelUpdatedRefTimeout.current);
                        }

                        // need at least 100 timeout to have for grid rows rendered
                        onModelUpdatedRefTimeout.current = setTimeout(() => onModelUpdatedRef?.current?.(event), 100);
                    }}
                    onGridReady={(event) => {
                        debouncedAutoSizeAllColumns({ event });

                        if (fullHeightGrid) {
                            setGridApi(event.api);
                        }

                        props.onGridReady?.(gridRef);

                        if (!setGridRef) return;
                        setGridRef(gridRef.current);
                    }}
                    onGridSizeChanged={(event) => {
                        debouncedAutoSizeAllColumns({ event });
                    }}
                    onRowDataUpdated={(event) => {
                        debouncedAutoSizeAllColumns({ event });
                    }}
                    onGridColumnsChanged={(event) => {
                        debouncedAutoSizeAllColumns({ event });
                    }}
                    onFirstDataRendered={(event) => {
                        debouncedAutoSizeAllColumns({ event });
                        onFirstDataRenderedRef?.current?.();

                        if (showSideHorizontalScrollIndicators) {
                            horizontalScrollArrowsRef.current?.refreshHorizontalScrollIndicators();
                            horizontalScrollArrowsRef.current?.updateArrowsVerticalPositions();
                        }
                    }}
                    onColumnMoved={(event) => {
                        onColumnMoved?.(event);
                    }}
                    onBodyScroll={(event) => {
                        onBodyScroll(event);
                    }}
                    onSelectionChanged={(event) => {
                        onRowSelected?.(event);
                    }}
                    tooltipShowDelay={tooltipShowDelay}
                    domLayout={useAutoHeight ? 'autoHeight' : 'normal'}
                    gridOptions={{
                        getContextMenuItems: function (params) {
                            var defaultItems = params.defaultItems;
                            if (!defaultItems) {
                                return [];
                            }
                            var result = defaultItems.filter((item) => item !== 'export'); // hide export option from context menu
                            return result;
                        },
                    }}
                    suppressDragLeaveHidesColumns // disable remove column by drag and drop it out of grid
                    suppressMultiSort
                    onSortChanged={(event) => {
                        onSortChanged(event);
                    }}
                    {...(typeof embedFullWidthRows === 'boolean' ? { embedFullWidthRows: embedFullWidthRows } : {})}
                    defaultCsvExportParams={defaultCsvExportParams}
                    autoSizePadding={autoSizePadding}
                    onColumnResized={(e) => {
                        debouncedOnColumnResized(e);

                        if (showSideHorizontalScrollIndicators) {
                            horizontalScrollArrowsRef.current?.refreshHorizontalScrollIndicators();
                        }
                    }}
                    rowBuffer={rowBuffer}
                    getMainMenuItems={getMainMenuItems}
                    tooltipInteraction={tooltipInteraction}
                    onColumnVisible={(event) => {
                        onColumnVisibleGetter?.(gridContainerRef)?.(event);

                        if (
                            preferencesConfiguration &&
                            enableSavingUserColumnsVisibility &&
                            ['columnMenu', 'toolPanelUi'].includes(event.source)
                        ) {
                            saveUserColumnsVisibility({
                                api: event.api,
                                setUserPreferences: userPreferencesRef.current?.setUserPreferences,
                                selector: preferencesConfiguration?.selectorConfiguration?.selector,
                                userPreferencesSavingMode,
                            });
                        }
                    }}
                />
                {useDragScroll && !isTouchDevice && (
                    <AgGridDragScroll
                        gridRef={gridRef}
                        gridContainerRef={gridContainerRef}
                        ref={setDragListenersRef}
                        useBrowserVerticalScroll={useBrowserVerticalScroll}
                    />
                )}
                {showSideHorizontalScrollIndicators && (
                    <AgGridHorizontalScrollArrows
                        gridRef={gridRef}
                        gridContainerRef={gridContainerRef}
                        ref={horizontalScrollArrowsRef}
                    />
                )}
                {preferencesConfiguration && (
                    <UserPreferences
                        ref={userPreferencesRef}
                        useUserPreferencesProps={preferencesConfiguration.useUserPreferencesProps}
                    />
                )}
            </div>
        </div>
    );
});
